import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { GripVertical } from "lucide-react";

const PAIRS = [
  {
    before: "https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/cscdop9v_IMG_3061.jpeg",
    after: "https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/3qfqn5am_IMG_3062.jpeg",
    label: "VW Scirocco Full Respray",
    desc: "Viper Green to Midnight Purple V2",
  },
  {
    before: "https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/4wzc82rd_IMG_3066.jpeg",
    after: "https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/poh89vd5_IMG_3056.jpeg",
    label: "R33 Skyline GTST",
    desc: "Blue to custom black with Pandem widebody kit",
  },
];

const BeforeAfterSlider = ({ before, after }) => {
  const containerRef = useRef(null);
  const [sliderPos, setSliderPos] = useState(50);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(5, Math.min(95, (x / rect.width) * 100));
    setSliderPos(pct);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => { if (isDragging.current) updatePosition(e.clientX); };
  const handleTouchMove = (e) => { e.preventDefault(); updatePosition(e.touches[0].clientX); };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[16/9] overflow-hidden cursor-col-resize select-none"
      style={{ touchAction: "none" }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      data-testid="before-after-slider"
    >
      {/* After image (full) */}
      <img src={after} alt="After" className="absolute inset-0 w-full h-full object-cover" />

      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
        <img src={before} alt="Before" className="absolute inset-0 w-full h-full object-cover" style={{ width: `${100 / (sliderPos / 100)}%`, maxWidth: "none" }} />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-10">
        <span className="bg-black/70 backdrop-blur-sm text-white text-xs tracking-[0.2em] uppercase font-body px-3 py-1.5">Before</span>
      </div>
      <div className="absolute top-4 right-4 z-10">
        <span className="bg-white/90 text-black text-xs tracking-[0.2em] uppercase font-body px-3 py-1.5">After</span>
      </div>

      {/* Slider line + handle */}
      <div
        className="absolute top-0 bottom-0 z-20 flex items-center"
        style={{ left: `${sliderPos}%`, transform: "translateX(-50%)" }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className="w-[2px] h-full bg-white/80" />
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg cursor-grab active:cursor-grabbing">
          <GripVertical size={16} className="text-black" />
        </div>
      </div>
    </div>
  );
};

export const BeforeAfterSection = () => {
  return (
    <section id="before-after" data-testid="before-after-section" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-[1px] bg-[#3b82f6]" />
            <span className="text-sm tracking-[0.3em] uppercase text-zinc-500 font-body font-medium">The Difference</span>
          </div>
          <h2 data-testid="before-after-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-[0.95]">
            Before & After
          </h2>
          <p className="text-zinc-500 font-body text-base mt-4">Drag the slider to see the transformation</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {PAIRS.map((pair, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              data-testid={`before-after-pair-${i}`}
            >
              <BeforeAfterSlider before={pair.before} after={pair.after} />
              <div className="bg-[#09090b] border border-white/5 border-t-0 p-5">
                <h3 className="font-heading text-lg uppercase tracking-wide text-white">{pair.label}</h3>
                <p className="text-zinc-500 font-body text-sm mt-1">{pair.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
